import { Controller } from '@hotwired/stimulus'
import { getJson } from '../../../modules/ajax'
import { COLOR_SCHEMES } from '../color_schemes'
import { TooltipManager } from '../tooltip_manager'
import { PieChartRenderer } from '../pie_chart_renderer'
import { ModalManager } from '../modal_manager'
import { SliderManager } from '../slider_manager'
import { StateManager } from '../state_manager'
import { ResultsRenderer } from '../results_renderer'
import { FilterParamsBuilder } from '../filter_params_builder'
import { OptionButtonManager } from '../option_button_manager'
import { SliderToggleManager } from '../slider_toggle_manager'
import { FormResetManager } from '../form_reset_manager'
import { isMobile, loadImages, debounce } from '../utils'

/**
 * Stimulus controller for the sleeving selector feature
 * Thin orchestration layer that coordinates UI managers and handles Hotwire interactions
 */
export default class extends Controller {
  static targets = [
    'results',
    'tooltip',
    'matchCount',
    'tempSlider',
    'expansionSlider',
    'abrasionSlider',
    'tempAmount',
    'expAmount',
    'abraAmount',
    'form'
  ]

  static values = {
    sources: Object,
    matchesText: String,
    noMatchesText: String,
    noResultsMessage: String
  }

  async connect() {
    this.colorSchemes = COLOR_SCHEMES
    this.images = await loadImages(this.sourcesValue)
    this.isMobileDevice = isMobile()
    
    // Initialize managers
    this.initializeManagers()
    
    // Debounce filter updates to prevent excessive requests
    this.debouncedUpdateFilters = debounce(this.updateFilters.bind(this), 300)
    
    this.sliderManager.initialize()
    
    // Restore saved state from sessionStorage
    this.stateManager.restore(this.sliderManager)
    
    // Load initial results on page load
    this.updateFilters()
  }

  /**
   * Initialize all manager instances
   * @private
   */
  initializeManagers() {
    this.tooltipManager = new TooltipManager(
      this.tooltipTarget,
      this.colorSchemes,
      this.resultsTarget,
      this.isMobileDevice
    )
    
    this.pieChartRenderer = new PieChartRenderer(
      this.images,
      this.colorSchemes,
      this.tooltipManager,
      this.isMobileDevice
    )
    
    this.modalManager = new ModalManager(this.colorSchemes, this.pieChartRenderer)
    
    this.sliderManager = new SliderManager(
      this.tempSliderTarget,
      this.expansionSliderTarget,
      this.abrasionSliderTarget,
      this.tempAmountTarget,
      this.expAmountTarget,
      this.abraAmountTarget
    )
    
    this.stateManager = new StateManager(this.element)
    
    this.resultsRenderer = new ResultsRenderer(
      this.resultsTarget,
      this.matchCountTarget,
      this.element,
      this.colorSchemes,
      this.pieChartRenderer,
      this.modalManager,
      this.isMobileDevice,
      {
        matchesText: this.matchesTextValue,
        noMatchesText: this.noMatchesTextValue,
        noResultsMessage: this.noResultsMessageValue
      }
    )
    
    this.filterParamsBuilder = new FilterParamsBuilder(this.element, this.sliderManager)
    
    this.optionButtonManager = new OptionButtonManager(this.element)
    
    this.sliderToggleManager = new SliderToggleManager(this.element)
    
    this.formResetManager = new FormResetManager(this.element, {
      optionButtonManager: this.optionButtonManager,
      sliderToggleManager: this.sliderToggleManager,
      sliderManager: this.sliderManager,
      stateManager: this.stateManager
    })
  }

  // Event handlers - delegate to managers
  
  /**
   * Handle slider display updates (called on input for real-time feedback)
   * @param {Event} event - Input event from slider
   */
  updateSliderDisplay(event) {
    this.sliderManager.updateSliderDisplay(event.target.closest('.dual-range-slider'))
  }

  /**
   * Handle slider value changes (called on change/mouseup to trigger filtering)
   * @param {Event} event - Change event from slider
   */
  sliderChanged(event) {
    this.sliderManager.updateSliderDisplay(event.target.closest('.dual-range-slider'))
    this.stateManager.save(this.sliderManager)
    this.debouncedUpdateFilters()
  }

  /**
   * Reset form to default state
   * @param {Event} event - Click event from reset button
   */
  reset(event) {
    event.preventDefault()
    this.formResetManager.reset()
    this.updateFilters()
  }

  /**
   * Handle option button selection
   * @param {Event} event - Click event from option button
   */
  selectOption(event) {
    const button = event.currentTarget
    this.optionButtonManager.selectOption(button)
    this.stateManager.save(this.sliderManager)
    this.updateFilters()
  }

  /**
   * Handle slider toggle switch
   * @param {Event} event - Change event from toggle checkbox
   */
  toggleSlider(event) {
    const toggle = event.currentTarget
    this.sliderToggleManager.toggleSlider(toggle)
    this.stateManager.save(this.sliderManager)
    this.updateFilters()
  }

  /**
   * Handle sort order changes
   * @param {Event} event - Change event from sort select
   */
  sortChanged(event) {
    this.stateManager.save(this.sliderManager)
    this.updateFilters()
  }

  // AJAX and rendering - delegate to specialized components
  
  /**
   * Update filter results from server
   */
  updateFilters() {
    const params = this.filterParamsBuilder.build()
    
    getJson(
      `/resources/sleeving-selector?${params}`,
      (data) => this.resultsRenderer.renderResults(data),
      () => this.resultsRenderer.renderError()
    )
  }
}
